"use strict";
/**
 * Canonical document filename derivation — the SINGLE source of truth for how a
 * Loom doc's on-disk filename is formed. Before this module the logic was spread
 * across ~10 disagreeing sites (per-type create use-cases, the core id generators,
 * and the `docPathInThread` fallback); they are now all routed here.
 *
 * Identity is ALWAYS the frontmatter ULID. Filenames are human-facing presentation
 * and may change freely (rename / migrate) without touching a single cross-reference
 * (`parent_id`, `child_ids`, `requires_load`, `blockedBy`, `depends_on`).
 *
 * New (canonical) scheme — flat, humanised, thread-local:
 *   idea      → idea.md
 *   design    → design.md
 *   plan      → plan-NNN.md          (NNN = zero-padded thread-local ordinal)
 *   done      → plan-NNN-done.md     (mirrors its plan's ordinal)
 *   chat      → chat-NNN.md
 *   req       → req.md
 *   thread    → thread.md
 *   reference → {slug}.md
 *
 * Dual-read (transition strategy A): the recognisers below ALSO match the legacy
 * names ({threadSlug}-idea.md, {threadSlug}-design.md, {threadSlug}-plan-NNN.md,
 * {threadSlug}-chat-NNN.md, {planId}-done.md) so a repo can be read correctly before
 * `loom migrate-layout` normalises it. Writers always emit the new scheme.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatOrdinal = formatOrdinal;
exports.nextOrdinal = nextOrdinal;
exports.planFileName = planFileName;
exports.doneFileName = doneFileName;
exports.chatFileName = chatFileName;
exports.singletonFileName = singletonFileName;
exports.isPlanFile = isPlanFile;
exports.isDoneFile = isDoneFile;
exports.isChatFile = isChatFile;
exports.isIdeaFile = isIdeaFile;
exports.isDesignFile = isDesignFile;
exports.planOrdinalFromFile = planOrdinalFromFile;
exports.chatOrdinalFromFile = chatOrdinalFromFile;
// These suffix-anchored patterns match BOTH the new flat names and the legacy
// thread-prefixed names, because the new name is a suffix of the legacy one
// (e.g. `plan-001.md` is the tail of `some-thread-plan-001.md`).
const PLAN_ORDINAL_RE = /plan-(\d+)\.md$/;
const CHAT_ORDINAL_RE = /chat-(\d+)\.md$/;
const DONE_RE = /-done\.md$/;
const IDEA_RE = /(?:^|-)idea\.md$/;
const DESIGN_RE = /(?:^|-)design\.md$/;
/** Zero-pad an ordinal to the canonical 3-digit width (gaps preserved, never renumbered). */
function formatOrdinal(n) {
    return String(n).padStart(3, '0');
}
/**
 * Next thread-local ordinal for a plan/chat: max existing (legacy OR new) + 1.
 * Deleted ordinals leave gaps — the counter never reuses a number, so a filename
 * stays stable for the life of the doc.
 */
function nextOrdinal(existingFilenames, type) {
    const re = type === 'plan' ? PLAN_ORDINAL_RE : CHAT_ORDINAL_RE;
    const nums = existingFilenames
        .map(f => f.match(re)?.[1])
        .filter((x) => Boolean(x))
        .map(Number);
    return nums.length ? Math.max(...nums) + 1 : 1;
}
// --- Canonical (new-scheme) filename writers -------------------------------
function planFileName(ordinal) {
    return `plan-${formatOrdinal(ordinal)}.md`;
}
function doneFileName(planOrdinal) {
    return `plan-${formatOrdinal(planOrdinal)}-done.md`;
}
function chatFileName(ordinal) {
    return `chat-${formatOrdinal(ordinal)}.md`;
}
/** The flat singleton filename for a per-thread doc type, or null if the type is ordinal/slug-named. */
function singletonFileName(type) {
    return `${type === 'thread' ? 'thread' : type}.md`;
}
// --- Dual-read recognisers (filename → doc type) ---------------------------
// Used by directory-scoped scanners; the containing folder (plans/ vs done/ vs
// chats/) further disambiguates legacy done docs that were named like plans.
function isPlanFile(f) {
    return PLAN_ORDINAL_RE.test(f) && !DONE_RE.test(f);
}
function isDoneFile(f) {
    return DONE_RE.test(f);
}
function isChatFile(f) {
    // new: chat-001.md · legacy: foo-chat-001.md / foo-chat.md · bare: chat.md
    return /(?:^|-)chat(?:-\d+)?\.md$/.test(f);
}
function isIdeaFile(f) {
    return IDEA_RE.test(f);
}
function isDesignFile(f) {
    return DESIGN_RE.test(f);
}
/** Extract a plan's ordinal from its filename (legacy or new), or null if absent. */
function planOrdinalFromFile(filename) {
    const m = filename.match(PLAN_ORDINAL_RE);
    return m ? Number(m[1]) : null;
}
/** Extract a chat's ordinal from its filename (legacy or new), or null if absent. */
function chatOrdinalFromFile(filename) {
    const m = filename.match(CHAT_ORDINAL_RE);
    return m ? Number(m[1]) : null;
}
